import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import HeaderServicesMegaMenu from './HeaderServicesMegaMenu';
import type { PodusProduct } from './productRegistry';

/** Inline chevron-down SVG to avoid react-icons dependency */
function ChevronDown({ rotated }: { rotated: boolean }) {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                transition: 'transform 200ms ease',
                transform: rotated ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
        >
            <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export interface ServicesNavButtonProps {
    /** Additional inline styles for the button */
    style?: React.CSSProperties;
    /** Additional CSS class for the button */
    className?: string;
    /** Override the default product list */
    products?: PodusProduct[];
    /** Label text (default: "Services") */
    label?: string;
}

export default function ServicesNavButton({
    style,
    className = '',
    products,
    label = 'Services',
}: ServicesNavButtonProps) {
    const [open, setOpen] = useState(false);
    const [menuTop, setMenuTop] = useState(0);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    // Calculate position when opening
    useEffect(() => {
        if (open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuTop(rect.bottom + 8);
        }
    }, [open]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                triggerRef.current &&
                !triggerRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    return (
        <>
            <button
                ref={triggerRef}
                onClick={handleToggle}
                className={className}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    color: 'inherit',
                    ...style,
                }}
            >
                {label}
                <ChevronDown rotated={open} />
            </button>

            {createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'fixed',
                        top: menuTop,
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        pointerEvents: open ? 'auto' : 'none',
                    }}
                >
                    <HeaderServicesMegaMenu
                        open={open}
                        dropdownRef={dropdownRef}
                        onClose={() => setOpen(false)}
                        products={products}
                    />
                </div>,
                document.body
            )}
        </>
    );
}
